import { Box, Stack, useMediaQuery, useTheme } from "@mui/material";
import { Download, Eye } from "lucide-react";
import { Button, palette, Typography } from "@powerdiary/components-library/lib";
import { useClientPortalConfigContext } from "../../../context";
import { ClientDocumentDto } from "types/generated/models";

interface IDocumentItemProps {
    document: ClientDocumentDto;
}

export default function DocumentItem({ document }: Readonly<IDocumentItemProps>) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { clientPortalGuid } = useClientPortalConfigContext();

    const buttonSx = { width: isMobile ? "100%" : "150px" };
    const baseUrl = `/api/v1/${clientPortalGuid}/Documents`;

    const handleView = () => {
        window.open(`${baseUrl}/${document.id}/View`, "_blank");
    };

    const handleDownload = () => {
        window.location.assign(`${baseUrl}/${document.id}/Download`);
    };

    return (
        <Box
            sx={{
                border: `1px solid ${palette.neutral["300"]}`,
                borderRadius: "4px",
                padding: "24px",
                width: "100%",
                minWidth: 0,
            }}
            data-test-id={`section_document_item_${document.id}`}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    gap: isMobile ? "16px" : "8px",
                }}
            >
                <Stack sx={{ flex: 1, gap: 0.75 }}>
                    <Typography variant="paragraphLargeSemiBold" data-test-id="text_document_name">
                        {document.fileName}
                    </Typography>
                    {document.description && (
                        <Typography
                            variant="paragraphMediumRegular"
                            color="text.secondary"
                            data-test-id="text_document_description"
                        >
                            {document.description}
                        </Typography>
                    )}
                    <Box sx={{ display: "flex", gap: 2 }}>
                        <Typography
                            variant="paragraphMediumRegular"
                            color="text.disabled"
                            data-test-id="text_document_upload_date"
                        >
                            Uploaded {new Date(document.uploadDate).toLocaleDateString()}
                        </Typography>
                        <Typography
                            variant="paragraphMediumRegular"
                            color="text.disabled"
                            data-test-id="text_document_file_size"
                        >
                            {document.fileSizeDisplay}
                        </Typography>
                    </Box>
                </Stack>
                <Stack
                    sx={{
                        justifyContent: "center",
                        alignItems: isMobile ? "stretch" : "flex-end",
                        gap: "8px",
                        flexShrink: 0,
                    }}
                >
                    <Button
                        variant="contained"
                        size="extraSmall"
                        onClick={handleView}
                        startIcon={<Eye size={14} strokeWidth={2} aria-hidden />}
                        sx={buttonSx}
                        data-test-id="button_view_document"
                    >
                        View
                    </Button>
                    <Button
                        variant="outlined"
                        size="extraSmall"
                        onClick={handleDownload}
                        startIcon={<Download size={14} strokeWidth={2} aria-hidden />}
                        sx={buttonSx}
                        data-test-id="button_download_document"
                    >
                        Download
                    </Button>
                </Stack>
            </Box>
        </Box>
    );
}
